/**
 * Clerk Portal — shared UI primitives.
 * All classes scoped under `.clerk-portal` (see styles.css).
 * Nothing here mutates global state.
 */
import React from 'react';
import { X, Search, AlertCircle, Check, Inbox } from 'lucide-react';

export const Card = ({ title, subtitle, actions, children, testId, className = '' }) => (
  <section className={`cp-card ${className}`.trim()} data-testid={testId}>
    {(title || actions) && (
      <header className="cp-card-header">
        <div>
          {title && <h3 className="cp-card-title">{title}</h3>}
          {subtitle && <p className="cp-card-subtitle">{subtitle}</p>}
        </div>
        {actions && <div>{actions}</div>}
      </header>
    )}
    {children}
  </section>
);

export const EmptyState = ({ icon: Icon = Inbox, title, message, action, testId }) => (
  <div className="cp-empty" data-testid={testId}>
    <div className="cp-empty-icon"><Icon size={18} strokeWidth={1.6} /></div>
    <p className="cp-empty-title">{title}</p>
    {message && <p className="cp-empty-lede">{message}</p>}
    {action && <div style={{ marginTop: 16 }}>{action}</div>}
  </div>
);

/**
 * variant = neutral | info | warn | ok | bad | returnable
 */
export const StatusPill = ({ variant = 'neutral', children, testId }) => (
  <span className={`cp-status cp-status-${variant}`} data-testid={testId}>{children}</span>
);

export const SearchInput = ({ value, onChange, placeholder, testId, autoFocus }) => (
  <div className="cp-search">
    <Search size={16} className="cp-search-icon" />
    <input
      type="text"
      className="cp-input"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      autoFocus={autoFocus}
      data-testid={testId}
    />
  </div>
);

export const Modal = ({ open, onClose, title, subtitle, wide, children, footer, testId }) => {
  if (!open) return null;
  return (
    <div className="cp-modal-scrim" onClick={onClose} role="dialog" aria-modal="true">
      <div
        className={`cp-modal${wide ? ' cp-modal-wide' : ''}`}
        onClick={(e) => e.stopPropagation()}
        data-testid={testId}
      >
        <header className="cp-modal-header">
          <div>
            <h3 className="cp-card-title">{title}</h3>
            {subtitle && <p className="cp-card-subtitle">{subtitle}</p>}
          </div>
          <button
            type="button"
            className="cp-topbar-menu-btn"
            onClick={onClose}
            aria-label="Close"
            data-testid="cp-modal-close-btn"
          ><X size={16} /></button>
        </header>
        <div className="cp-modal-body">{children}</div>
        {footer && <div className="cp-modal-footer">{footer}</div>}
      </div>
    </div>
  );
};

export const Toast = ({ toast, onDismiss }) => {
  if (!toast) return null;
  const cls = toast.variant === 'ok' ? 'cp-toast cp-toast-ok'
            : toast.variant === 'bad' ? 'cp-toast cp-toast-bad'
            : 'cp-toast';
  const Icon = toast.variant === 'bad' ? AlertCircle : Check;
  return (
    <div className={cls} role="status" data-testid="cp-toast">
      <Icon size={18} style={{ flexShrink: 0, marginTop: 1 }} />
      <div style={{ flex: 1 }}>{toast.message}</div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        style={{ background: 'transparent', border: 0, color: 'inherit', cursor: 'pointer', opacity: 0.7 }}
      ><X size={16} /></button>
    </div>
  );
};

/**
 * Format an ISO/Date-ish value as a short "dd Mmm · HH:mm" indian-ish label.
 * Renders "—" when null/undefined.
 */
export const formatShortTime = (raw) => {
  if (!raw) return '—';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '—';
  const day = d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  const time = d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false });
  return `${day} · ${time}`;
};

export const formatDayOnly = (raw) => {
  if (!raw) return '—';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

/**
 * Return the short reference id form of a UUID-ish string. E.g.
 *   "a1b2c3d4-...-..."  →  "REQ-A1B2C3D4"
 */
export const shortRef = (id, prefix = 'REF') => {
  if (!id) return `${prefix}-—`;
  const s = String(id).replace(/-/g, '').slice(0, 8).toUpperCase();
  return `${prefix}-${s}`;
};

/**
 * Map return condition slug → user-facing label.
 * These are the four condition values the existing backend accepts.
 */
export const CONDITION_LABEL = {
  unused: 'Unused',
  used_good: 'Used · Good',
  damaged: 'Damaged',
  lost: 'Lost',
};

export const conditionVariant = (raw) => {
  const s = (raw || '').toLowerCase();
  if (s === 'unused') return 'ok';
  if (s === 'used_good' || s === 'used-good' || s === 'used') return 'info';
  if (s === 'damaged') return 'bad';
  if (s === 'lost') return 'bad';
  return 'neutral';
};

export const conditionLabel = (raw) => {
  if (!raw) return '—';
  const s = raw.toLowerCase().replace(/-/g, '_');
  return CONDITION_LABEL[s] || raw;
};
